import React from 'react';
import { useSelector } from "react-redux";
import { base_url } from "../utils/axiosConfig";
import { motion } from 'framer-motion';
import { MdTrendingUp, MdArrowForward } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

const BestSelling = () => {
  const productState = useSelector((state) => state.product.product);
  const navigate = useNavigate();

  const ORANGE = '#ff6701';
  const DARK = '#232f3e';

  const styles = {
    section: {
      padding: '60px 0',
      backgroundColor: '#f8f9fa',
      fontFamily: "'Rubik', sans-serif",
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginBottom: '40px',
    },
    titleSection: {
      textAlign: 'left',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: `${ORANGE}15`,
      color: ORANGE,
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '12px',
    },
    title: {
      fontSize: '32px',
      fontWeight: '800',
      color: DARK,
      margin: 0,
      lineHeight: 1.2,
    },
    viewAll: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: ORANGE,
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'gap 0.3s ease',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '24px',
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      position: 'relative',
      border: '1px solid #f0f0f0',
    },
    imageContainer: {
      width: '100%',
      height: '220px',
      overflow: 'hidden',
      position: 'relative',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.6s ease',
    },
    content: {
      padding: '20px',
    },
    productTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: DARK,
      marginBottom: '8px',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      lineHeight: 1.4,
      height: '44px',
    },
    priceWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '15px',
    },
    price: {
      fontSize: '20px',
      fontWeight: '700',
      color: ORANGE,
      margin: 0,
    },
    currency: {
      fontSize: '14px',
      marginRight: '2px',
    },
    buyBtn: {
      backgroundColor: DARK,
      color: '#fff',
      padding: '8px 16px',
      borderRadius: '10px',
      fontSize: '13px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    noProducts: {
      textAlign: 'center',
      color: '#888',
      padding: '40px 0',
      fontSize: '16px',
    },
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <div style={styles.badge}>
              <MdTrendingUp /> Bestsellers
            </div>
            <h2 style={styles.title}>Top Selling Products</h2>
          </div>
          <div 
            style={styles.viewAll} 
            onClick={() => navigate('/product')}
            onMouseEnter={(e) => e.currentTarget.style.gap = '10px'}
            onMouseLeave={(e) => e.currentTarget.style.gap = '6px'}
          >
            Explore All <MdArrowForward />
          </div>
        </div>

        {productState && productState.length > 0 ? (
          <div style={styles.grid}>
            {productState.filter(product => {
              const selectedVariant = product.variants?.[0] || null;
              const currentPrice = selectedVariant ? selectedVariant.CurrentPrice : product.CurrentPrice;
              return currentPrice && currentPrice > 0;
            }).slice(0, 4).map((product, index) => {
              const selectedVariant = product.variants?.[0] || null;
              const currentPrice = selectedVariant ? selectedVariant.CurrentPrice : product.CurrentPrice;
              const offerPrice = selectedVariant ? selectedVariant.offerPrice : product.offerPrice;
              const displayPrice = offerPrice && offerPrice > 0 ? offerPrice : currentPrice;
              const showOldPrice = offerPrice > 0 && currentPrice > 0 && offerPrice < currentPrice;
              
              return (
              <motion.div
                key={product._id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                style={styles.card}
                onClick={() => navigate(`/product/${product?._id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.05)';
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1)';
                }}
              >
                <div style={styles.imageContainer}>
                  <img
                    src={`${base_url}${product.images?.[0]?.url}`}
                    style={styles.image}
                    alt={product.title}
                  />
                </div>
                <div style={styles.content}>
                  <h5 style={styles.productTitle}>{product.title}</h5>
                  <div style={styles.priceWrapper}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {showOldPrice ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <p style={styles.price}><span style={styles.currency}>₹</span>{displayPrice}</p>
                          <small style={{ textDecoration: 'line-through', color: '#999', fontSize: '13px' }}>₹{currentPrice}</small>
                        </div>
                      ) : (
                         <p style={styles.price}><span style={styles.currency}>₹</span>{displayPrice}</p>
                      )}
                      {selectedVariant && (
                        <span style={{ fontSize: '12px', color: '#666' }}>/ {selectedVariant.measurementUnit} {selectedVariant.unit?.title}</span>
                      )}
                    </div>
                    <div style={styles.buyBtn}>View Details</div>
                  </div>
                </div>
              </motion.div>
            )})}
          </div>
        ) : (
          <div style={styles.noProducts}>
            We're restocking our best sellers. Check back soon!
          </div>
        )}
      </div>
    </section>
  );
};

export default BestSelling;
